/**
 * Villager (vanilla Java): 20 HP, 0.6 x 1.95, movement 0.5 attribute.
 *
 * Simplified brain (vanilla uses a full Brain with schedules and memories):
 *  - claims the nearest unclaimed workstation within 16 blocks for a
 *    profession (unemployed only; nitwits never), and the nearest free bed
 *    within 32 blocks as home;
 *  - day: wanders near home, visits the workstation (restocks there, at most
 *    twice per day), watches nearby players;
 *  - night: walks to its bed and sleeps in it (lies down), or stands still;
 *  - flees zombies / zombie villagers (8 blocks) and illagers (12), panics
 *    for 5 s when hurt, and may summon an iron golem while scared;
 *  - trades through the trade screen; levels up on closing it once the XP
 *    threshold is reached (2 new offers per level).
 * Babies (big-headed, half size) grow up in 20 min.
 */

import { I } from '../../game/itemIds';
import {
  levelForXp, offerFromRow, offerToRow, PROFESSIONS, professionIndex, restock, rollOffers, needsRestock,
  WORKSTATIONS, type Offer, type Profession,
} from '../../game/trading';
import type { Stack } from '../../game/types';
import type { MobCtx } from '../mob';
import type { Mob } from '../mob';
import { BED_FOOT_ID, collisionOf } from '../../blocks';
import { cellKey, claims, findBlock, isBed, playSound, pruneClaims, vh, VILLAGER_THREATS, VMob } from './ctx';

export const BABY_GROW_TICKS = 24000;
/** Vanilla food values for willingness: bread 4, carrot / potato / beetroot 1; 12 to breed. */
export const VILLAGER_FOOD: ReadonlyMap<number, number> = new Map([[I.BREAD, 4], [I.CARROT, 1], [I.POTATO, 1], [I.BEETROOT, 1]]);
export const BREED_FOOD_NEEDED = 12;
const RESTOCK_GAP = 6000;

/** Vanilla villager schedule (ticks of the day, 0 = sunrise). */
export type Phase = 'idle' | 'work' | 'meet' | 'home' | 'sleep';
export function schedulePhase(t: number): Phase {
  t = ((t % 24000) + 24000) % 24000;
  if (t >= 12000) return 'sleep';
  if (t >= 11000) return 'home';
  if (t >= 9000) return 'meet';
  if (t >= 2000) return 'work';
  return 'idle';
}

export interface SleepPose { x: number; y: number; z: number; yaw: number; dx: number; dz: number }
/**
 * Where a villager lies for a bed whose head half is at (hx, hy, hz) and whose
 * foot half is one step along (-dx, -dz): feet at the foot end of the mattress,
 * head on the pillow, `yaw` turning the model's up axis toward the pillow.
 */
export function sleepPose(hx: number, hy: number, hz: number, dx: number, dz: number): SleepPose {
  // villager is ~1.95 long, the bed 2: start 0.03 inside the foot edge
  const along = 1.47;
  return { x: hx + 0.5 - dx * along, y: hy + 9 / 16, z: hz + 0.5 - dz * along, yaw: Math.atan2(dx, dz), dx, dz };
}

/** Direction foot -> head of the bed whose head half is at (x, y, z), or null if the foot is missing. */
export function bedDirection(get: (x: number, y: number, z: number) => number, x: number, y: number, z: number): [number, number] | null {
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    if (get(x - dx, y, z - dz) === BED_FOOT_ID) return [dx, dz];
  }
  return null;
}

/** Golem-summon hook (VillageSystem): scared villager at (x, z). */
export const villagerHooks: { scared: ((v: Villager) => void) | null; angerGolems: ((x: number, y: number, z: number) => void) | null } = {
  scared: null, angerGolems: null,
};

export class Villager extends VMob {
  vtype = 0;
  profession: Profession = 'none';
  level = 1;
  xp = 0;
  offers: Offer[] = [];
  baby = false;
  growTicks = 0;
  food = 0;
  breedCooldown = 0;
  work: [number, number, number] | null = null;
  home: [number, number, number] | null = null;
  /** Village meeting point (bell / well), set when the village is populated. */
  meet: [number, number, number] | null = null;
  private pose: SleepPose | null = null;
  sleeping = false;
  /** Set while a player has the trade screen open. */
  trading = false;
  /** Head-shake ticks (no trades / baby clicked). */
  shake = 0;
  private lastRestock = -RESTOCK_GAP;
  private restocks = 0;
  private wasNight = false;
  private lastDayT = 0;
  private fleeFrom: Mob | null = null;
  private scanCd: number;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'villager', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 20, speed: 0.5 }, rnd);
    this.scanCd = Math.floor(rnd() * 100);
  }

  get canTrade(): boolean {
    return !this.baby && this.profession !== 'none' && this.profession !== 'nitwit' && this.offers.length > 0;
  }

  setBaby(b: boolean): void {
    this.baby = b;
    this.growTicks = b ? BABY_GROW_TICKS : 0;
    const self = this as unknown as { width: number; height: number; eyeHeight: number };
    self.width = b ? 0.3 : 0.6; self.height = b ? 0.975 : 1.95; self.eyeHeight = b ? 0.81 : 1.62;
  }

  setProfession(p: Profession, rnd: () => number): void {
    this.profession = p;
    if (p === 'none' || p === 'nitwit') { this.offers = []; this.level = 1; this.xp = 0; return; }
    if (this.offers.length === 0) {
      this.level = Math.max(1, this.level);
      for (let l = 1; l <= this.level; l++) this.offers.push(...rollOffers(p, l, rnd));
    }
  }

  /** Vanilla: after trading, level up when the XP threshold is reached (one level per close). */
  onTradeClosed(rnd: () => number): void {
    this.trading = false;
    while (this.level < 5 && levelForXp(this.xp) > this.level) {
      this.level++;
      this.offers.push(...rollOffers(this.profession, this.level, rnd));
      this.health = this.maxHealth; // vanilla: 10 s of Regeneration
      vh.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
    }
  }

  override onHurtBy(): void {
    this.panicTicks = 100;
    villagerHooks.angerGolems?.(this.x, this.y, this.z);
    playSound('entity.villager.hurt', this);
  }

  onHurtByMob(): void { this.panicTicks = 100; }

  override knockback(dx: number, dz: number, s: number, rnd: () => number): void {
    super.knockback(dx, dz, s, rnd);
  }

  xpValue(): number { return 0; }

  /** Villagers open doors on the way and shut them behind. */
  protected override doorMode(): 0 | 1 | 2 { return 1; }

  protected ai(ctx: MobCtx): void {
    const rnd = ctx.host.rnd;
    if (this.baby && --this.growTicks <= 0) this.setBaby(false);
    if (this.breedCooldown > 0) this.breedCooldown--;
    if (this.shake > 0) this.shake--;
    const night = ctx.host.isNight();
    const dt0 = ctx.host.dayTime?.();
    if (dt0 !== undefined) { if (dt0 < this.lastDayT) this.restocks = 0; this.lastDayT = dt0; if (dt0 >= 9000 && dt0 < 9020) this.restocks = Math.min(this.restocks, 1); }
    if (night !== this.wasNight) { this.wasNight = night; if (!night && dt0 === undefined) this.restocks = 0; }

    if (--this.scanCd <= 0) { this.scanCd = 100; this.scan(ctx); }

    if (this.trading) {
      this.wake(ctx);
      const p = ctx.host.player;
      this.lookAt(p.x, p.eyeY, p.z);
      this.faceToward(p.x, p.z);
      return;
    }

    // threats: flee
    if (this.phase(ctx, 10) || (this.fleeFrom && !this.fleeFrom.alive)) {
      this.fleeFrom = this.nearest(12, (m) => VILLAGER_THREATS.has(m.type)
        && ((m.x - this.x) ** 2 + (m.z - this.z) ** 2 < ((m.type === 'zombie' || m.type === 'zombie_villager' || m.type === 'vindicator') ? 64 : 144)));
    }
    if (this.fleeFrom || this.panicTicks > 0) {
      this.wake(ctx);
      if (this.panicTicks > 0) this.panicTicks--;
      if (this.fleeFrom) this.goAway(this.fleeFrom.x, this.fleeFrom.z, 0.9);
      else {
        if (this.panicTicks % 30 === 29 || this.panicAngle === 0) this.panicAngle = rnd() * Math.PI * 2 + 0.001;
        if (this.safeAhead(Math.cos(this.panicAngle), Math.sin(this.panicAngle), 3, true)) this.setMove(Math.cos(this.panicAngle), Math.sin(this.panicAngle), 0.9);
      }
      if (this.phase(ctx, 40)) villagerHooks.scared?.(this);
      return;
    }

    const dayT = ctx.host.dayTime?.() ?? (night ? 14000 : 5000);
    const phase = schedulePhase(dayT);
    this.phaseNow = phase;
    // sleep at night; head home at dusk (and out of the rain)
    if (phase === 'sleep' || (night && phase !== 'idle')) { this.nightAi(ctx, true); return; }
    if (phase === 'home' || this.rainingHere(ctx)) { this.nightAi(ctx, false); return; }
    this.wake(ctx);

    const hasJob = !!this.work && this.profession !== 'none' && this.profession !== 'nitwit';
    // work 2000-9000: walk to the job site and stay around it
    if (phase === 'work' && hasJob) {
      const [wx, wy, wz] = this.work!;
      const d2 = (wx + 0.5 - this.x) ** 2 + (wz + 0.5 - this.z) ** 2;
      if (d2 > 1.8 * 1.8) { this.goToward(wx + 0.5, wz + 0.5, 0.5, true, wy); this.lookAt(wx + 0.5, wy + 0.5, wz + 0.5); return; }
      this.atSite = true;
      this.lookAt(wx + 0.5, wy + 0.5, wz + 0.5);
      this.atWorkstation(ctx);
      if (this.profession === 'librarian' || this.profession === 'farmer') this.stroll(ctx, 0.3, 2, true);
      return;
    }
    // meet 9000-11000: gather at the village meeting point
    if (phase === 'meet' && this.meet) {
      const [mx, my, mz] = this.meet;
      const d2 = (mx + 0.5 - this.x) ** 2 + (mz + 0.5 - this.z) ** 2;
      if (d2 > 5 * 5) { this.goToward(mx + 0.5, mz + 0.5, 0.5, true, my); return; }
      this.stroll(ctx, 0.3, 2, true);
      this.lookAtNearPlayer(ctx);
      return;
    }

    // stay near home
    const anchor = this.home ?? this.work;
    if (anchor) {
      const d2 = (anchor[0] - this.x) ** 2 + (anchor[2] - this.z) ** 2;
      if (d2 > 40 * 40) { this.goToward(anchor[0] + 0.5, anchor[2] + 0.5, 0.5, true, anchor[1]); return; }
    }
    this.stroll(ctx, this.baby ? 0.7 : 0.45, 2, true);
    this.lookAtNearPlayer(ctx);
  }

  phaseNow: Phase = 'idle';
  atSite = false;

  private nightAi(ctx: MobCtx, sleepTime: boolean): void {
    const h = this.home;
    if (!h) { this.wake(ctx); this.lookAtNearPlayer(ctx); if (ctx.host.rnd() < 0.3) this.stroll(ctx, 0.3, 2, true); return; }
    const bx = h[0] + 0.5, bz = h[2] + 0.5;
    const d2 = (bx - this.x) ** 2 + (bz - this.z) ** 2;
    if (this.sleeping) {
      if (!sleepTime) { this.wake(ctx); return; }
      this.lieDown(ctx);
      return;
    }
    if (sleepTime && d2 < 1.3 * 1.3 && Math.abs(this.y - h[1]) < 1.6) {
      this.lieDown(ctx);
      return;
    }
    if (!sleepTime && d2 < 3 * 3) { this.stroll(ctx, 0.25, 2, true); return; }
    this.goToward(bx, bz, 0.5, true, h[1]);
  }

  /** Lie in the claimed bed: feet at the foot end, head on the pillow. */
  private lieDown(ctx: MobCtx): void {
    const h = this.home!;
    const dir = bedDirection((x, y, z) => ctx.world.getBlock(x, y, z), h[0], h[1], h[2]);
    if (!dir) { this.sleeping = false; return; }
    const p = this.pose = sleepPose(h[0], h[1], h[2], dir[0], dir[1]);
    if (!this.sleeping) this.setSize(this.baby ? 0.3 : 0.6, 0.5);
    this.sleeping = true;
    this.x = p.x; this.z = p.z; this.y = p.y;
    this.vx = 0; this.vz = 0; this.vy = 0;
    this.yaw = this.headYaw = p.yaw;
  }

  /** Get up and stand beside the bed. */
  private wake(ctx: MobCtx): void {
    if (!this.sleeping) return;
    this.sleeping = false;
    this.setSize(this.baby ? 0.3 : 0.6, this.baby ? 0.975 : 1.95);
    const h = this.home, p = this.pose;
    this.pose = null;
    if (!h || !p) return;
    const w = ctx.world;
    // the cells to either side of the head half
    for (const [sx, sz] of [[-p.dz, p.dx], [p.dz, -p.dx], [p.dx, p.dz]] as const) {
      const cx = h[0] + sx, cz = h[2] + sz;
      if (collisionOf(w.getBlock(cx, h[1], cz)).length === 0 && collisionOf(w.getBlock(cx, h[1] + 1, cz)).length === 0
        && collisionOf(w.getBlock(cx, h[1] - 1, cz)).length > 0) {
        this.x = cx + 0.5; this.z = cz + 0.5; this.y = h[1];
        return;
      }
    }
    this.x = h[0] + 0.5; this.z = h[2] + 0.5; this.y = h[1] + 9 / 16 + 0.01;
  }

  private setSize(width: number, height: number): void {
    const self = this as unknown as { width: number; height: number; eyeHeight: number };
    self.width = width; self.height = height; self.eyeHeight = height * (1.62 / 1.95);
  }

  private atWorkstation(ctx: MobCtx): void {
    const t = ctx.tick;
    if (needsRestock(this.offers) && this.restocks < 2 && t - this.lastRestock >= RESTOCK_GAP) {
      restock(this.offers);
      this.restocks++;
      this.lastRestock = t;
      playSound(`entity.villager.work_${this.profession}`, this);
    }
  }

  /** Claim / validate workstation and bed. */
  private scan(ctx: MobCtx): void {
    const w = ctx.world;
    if (this.work) {
      const [x, y, z] = this.work;
      const id = w.getBlock(x, y, z);
      if (WORKSTATIONS.get(id) !== this.profession && !(this.profession === 'none')) {
        claims.delete(cellKey(x, y, z));
        this.work = null;
        // vanilla: a villager that never traded loses its profession with the workstation
        if (this.xp === 0 && this.level === 1 && this.profession !== 'nitwit') this.setProfession('none', ctx.host.rnd);
      }
    }
    if (!this.work || !this.home) pruneClaims(this.id);
    if (!this.baby && !this.work && this.profession !== 'nitwit') {
      const want = this.profession;
      const hit = findBlock(w, this.x, this.y, this.z, 24, 4,
        (id) => { const p = WORKSTATIONS.get(id); return p !== undefined && (want === 'none' || p === want); }, this.id);
      if (hit) {
        this.work = hit;
        claims.set(cellKey(...hit), this.id);
        if (this.profession === 'none') {
          this.setProfession(WORKSTATIONS.get(w.getBlock(...hit))!, ctx.host.rnd);
          playSound(`entity.villager.work_${this.profession}`, this);
          ctx.host.effect('heart', this.x, this.y + this.height + 0.3, this.z);
        }
      }
    }
    if (this.home && !isBed(w.getBlock(...this.home))) { claims.delete(cellKey(...this.home)); this.home = null; if (this.sleeping) { this.sleeping = false; this.pose = null; this.setSize(this.baby ? 0.3 : 0.6, this.baby ? 0.975 : 1.95); } }
    if (!this.home) {
      const hit = findBlock(w, this.x, this.y, this.z, 24, 4, isBed, this.id);
      if (hit) { this.home = hit; claims.set(cellKey(...hit), this.id); }
    }
  }

  /** Free claimed cells (on removal). */
  releaseClaims(): void {
    if (this.work) claims.delete(cellKey(...this.work));
    if (this.home) claims.delete(cellKey(...this.home));
  }

  override syncView(host: Parameters<VMob['syncView']>[0]): void {
    // color packs: type (3 bits) | profession (4) | level (3) | sleeping (1)
    this.color = (this.vtype & 7) | (professionIndex(this.profession) << 3) | (Math.min(5, this.level) << 7) | (this.sleeping ? 1 << 10 : 0);
    this.swell = this.shake / 20;
    super.syncView(host);
    this.view.baby = this.baby;
    if (this.sleeping && this.pose) { this.view.yaw = this.view.headYaw = this.pose.yaw; this.view.pitch = 0; this.view.px = this.x; this.view.py = this.y; this.view.pz = this.z; }
  }

  loot(): Stack[] { return []; }

  saveExtra(): unknown {
    return {
      t: this.vtype, p: this.profession, l: this.level, x: this.xp, f: this.food,
      b: this.baby ? this.growTicks : 0, o: this.offers.map(offerToRow),
      w: this.work, h: this.home, m: this.meet,
    };
  }

  loadExtra(d: unknown): void {
    const o = d as Record<string, unknown>;
    if (!o || typeof o !== 'object') return;
    const n = (v: unknown, def = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : def);
    this.vtype = n(o.t) & 7;
    this.profession = (PROFESSIONS as readonly string[]).includes(o.p as string) ? o.p as Profession : 'none';
    this.level = Math.max(1, Math.min(5, n(o.l, 1)));
    this.xp = n(o.x); this.food = n(o.f);
    if (n(o.b) > 0) { this.setBaby(true); this.growTicks = n(o.b); }
    this.offers = Array.isArray(o.o) ? (o.o as unknown[]).map(offerFromRow).filter((x): x is Offer => x !== null) : [];
    const cell = (v: unknown): [number, number, number] | null => (Array.isArray(v) && v.length === 3 ? [n(v[0]), n(v[1]), n(v[2])] : null);
    this.work = cell(o.w); this.home = cell(o.h); this.meet = cell(o.m);
    if (this.work) claims.set(cellKey(...this.work), this.id);
    if (this.home) claims.set(cellKey(...this.home), this.id);
  }
}
